import type {
  ClubSummary,
  ConversationPage,
  DirectMessageDto,
  DirectMessagePayload,
  FriendAddedPayload,
  FriendRelation,
  FriendRemovedPayload,
  FriendRequestDto,
  FriendSummary,
  PresencePayload,
  RequestReceivedPayload,
  RequestResolvedPayload,
  SocialSnapshot,
} from "@clubap/shared";

/**
 * Cómo el estado social del cliente cambia, sin React de por medio.
 *
 * Vive aparte del hook a propósito: es lógica con carreras dentro —un aviso y una foto
 * de la base compitiendo— y sacarla de los efectos es lo que permite probarla con un
 * script, en un repositorio que no tiene entorno de tests de componentes.
 *
 * Dos reglas gobiernan todo lo de acá:
 *
 *  - **Aplicar dos veces el mismo hecho da lo mismo.** El mismo mensaje llega por el
 *    aviso en vivo y por la respuesta HTTP, y a veces en el orden contrario al esperado.
 *  - **Una foto de la base no se mezcla con avisos: se descarta si se cruzó con uno.**
 *    Reaplicar un aviso viejo encima de una foto más nueva la ensuciaría —una presencia
 *    retrasada volvería a apagar a alguien que ya volvió—, y las horas no sirven para
 *    ordenarlos: `Date.now()` tiene resolución de milisegundo. Quien decide es
 *    `useSocial`, contando eventos durante el viaje.
 */

export interface Thread {
  messages: DirectMessageDto[];
  /** Cursor de lo más viejo, o null si ya no hay más que traer. */
  nextCursor: string | null;
  loading: boolean;
}

export interface SocialState {
  /** El snapshot llegó al menos una vez. Antes de eso la libreta está vacía, no sola. */
  ready: boolean;
  /** El canal en vivo está arriba. Sin él todo sigue funcionando, sólo sin avisos. */
  connected: boolean;
  friends: FriendSummary[];
  incoming: FriendRequestDto[];
  outgoing: FriendRequestDto[];
  clubs: ClubSummary[];
  threads: Record<string, Thread>;
  /** Con quién está abierta la conversación, o ninguna. */
  activePeer: string | null;
  error: string | null;
}

export const EMPTY_SOCIAL: SocialState = {
  ready: false,
  connected: false,
  friends: [],
  incoming: [],
  outgoing: [],
  clubs: [],
  threads: {},
  activePeer: null,
  error: null,
};

export type SocialTransform = (state: SocialState) => SocialState;

/**
 * Techo por conversación, y el ÚNICO. Al alcanzarlo se deja de ofrecer más historial,
 * en vez de crecer sin límite mientras alguien mantiene una ventana abierta.
 *
 * Hubo un segundo tope, más bajo, para lo que llegaba en vivo — y era un error: tras
 * cargar mil mensajes hacia atrás, uno nuevo recortaba el hilo a doscientos y ochocientos
 * desaparecían de la pantalla de golpe.
 */
export const MAX_THREAD_HISTORY = 1000;

const EMPTY_THREAD: Thread = { messages: [], nextCursor: null, loading: false };

/** Reemplaza al amigo con el mismo id, o lo agrega. Aplicarlo dos veces da lo mismo. */
export function upsertFriend(list: FriendSummary[], friend: FriendSummary): FriendSummary[] {
  return [...list.filter((entry) => entry.id !== friend.id), friend];
}

/** Agrega una solicitud si no estaba. La respuesta HTTP y el aviso traen la misma. */
export function upsertRequest(list: FriendRequestDto[], request: FriendRequestDto): FriendRequestDto[] {
  return list.some((entry) => entry.id === request.id) ? list : [...list, request];
}

function byTime(a: DirectMessageDto, b: DirectMessageDto): number {
  return a.at !== b.at ? a.at - b.at : a.id.localeCompare(b.id);
}

/** Une dos listas de mensajes por id, en orden. Duplicar la entrada no duplica nada. */
export function unionMessages(
  a: readonly DirectMessageDto[],
  b: readonly DirectMessageDto[],
): DirectMessageDto[] {
  const byId = new Map<string, DirectMessageDto>();
  for (const message of [...a, ...b]) byId.set(message.id, message);
  return [...byId.values()].sort(byTime);
}

/**
 * Mete un mensaje en su hilo sin duplicarlo.
 *
 * La dedupe es por el ID PERSISTENTE, no por posición ni por hora: el aviso en vivo y
 * la página de historial pueden traer el mismo mensaje, y con varias pestañas abiertas
 * eso deja de ser hipotético.
 */
export function mergeMessage(thread: Thread | undefined, message: DirectMessageDto): Thread {
  const base = thread ?? EMPTY_THREAD;
  if (base.messages.some((entry) => entry.id === message.id)) return base;

  return { ...base, messages: unionMessages(base.messages, [message]).slice(-MAX_THREAD_HISTORY) };
}

/**
 * Mezcla una página de historial con lo que el hilo ya tenga.
 *
 * **No reemplaza.** Entre que se pide la página y llega la respuesta puede entrar un
 * mensaje en vivo, y escribir encima la página lo borraría de la pantalla aunque esté
 * guardado en la base.
 */
export function mergePage(thread: Thread | undefined, page: ConversationPage): Thread {
  const merged = unionMessages(page.messages, (thread ?? EMPTY_THREAD).messages);

  // Al llegar al techo se deja de ofrecer historial: el botón desaparece en vez de
  // quedarse pidiendo páginas que ya no caben.
  const capped = merged.length > MAX_THREAD_HISTORY;

  return {
    messages: capped ? merged.slice(-MAX_THREAD_HISTORY) : merged,
    nextCursor: capped ? null : page.nextCursor,
    loading: false,
  };
}

/** Deja el hilo listo para recibir en vivo mientras se pide su primera página. */
export function openingThread(state: SocialState, peerId: string): SocialState {
  const thread = state.threads[peerId] ?? EMPTY_THREAD;

  return {
    ...state,
    activePeer: peerId,
    // Se crea AQUÍ y no al llegar la página: si no existiera, un mensaje que entre
    // mientras la página viaja no tendría dónde guardarse y se perdería de la pantalla.
    threads: { ...state.threads, [peerId]: { ...thread, loading: true } },
  };
}

/** En línea primero, luego lo más reciente: el orden en que se busca a alguien. */
function byPresenceThenRecency(a: FriendSummary, b: FriendSummary): number {
  if (a.online !== b.online) return a.online ? -1 : 1;
  const recency = (b.lastMessageAt ?? 0) - (a.lastMessageAt ?? 0);
  return recency !== 0 ? recency : a.username.localeCompare(b.username);
}

export function sortFriends(list: FriendSummary[]): FriendSummary[] {
  return [...list].sort(byPresenceThenRecency);
}

// --------------------------------------------------- transformaciones de evento

/*
 * Los cambios en vivo REORDENAN la lista.
 *
 * La UI promete "en línea primero, luego lo más reciente", y sin volver a ordenar esa
 * promesa sólo se cumplía justo después de una foto de la base: alguien que se conecta
 * se quedaba abajo del todo hasta la siguiente reconciliación.
 */
export function presenceChanged(payload: PresencePayload): SocialTransform {
  return (state) => ({
    ...state,
    friends: sortFriends(
      state.friends.map((friend) =>
        friend.id === payload.accountId ? { ...friend, online: payload.online } : friend,
      ),
    ),
  });
}

export function requestReceived(payload: RequestReceivedPayload): SocialTransform {
  return (state) => ({ ...state, incoming: upsertRequest(state.incoming, payload.request) });
}

export function requestResolved(payload: RequestResolvedPayload): SocialTransform {
  return (state) => ({
    ...state,
    incoming: state.incoming.filter((entry) => entry.id !== payload.requestId),
    outgoing: state.outgoing.filter((entry) => entry.id !== payload.requestId),
  });
}

export function friendAdded(payload: FriendAddedPayload): SocialTransform {
  return (state) => ({
    ...state,
    friends: sortFriends(upsertFriend(state.friends, payload.friend)),
    incoming: state.incoming.filter((entry) => entry.from.id !== payload.friend.id),
    outgoing: state.outgoing.filter((entry) => entry.to.id !== payload.friend.id),
  });
}

export function friendRemoved(payload: FriendRemovedPayload): SocialTransform {
  return (state) => {
    const threads = { ...state.threads };
    // La conversación deja de poder leerse, así que tampoco se queda en memoria.
    delete threads[payload.accountId];

    return {
      ...state,
      friends: state.friends.filter((friend) => friend.id !== payload.accountId),
      threads,
      activePeer: state.activePeer === payload.accountId ? null : state.activePeer,
    };
  };
}

/**
 * Un mensaje directo, mío o del otro.
 *
 * No sube el contador si el mensaje es mío o si su conversación está abierta: tenerla
 * delante es haberlo leído. El hilo sólo lo guarda si ya existía — `openingThread` lo
 * crea al abrir, justo para que no haya un hueco mientras la página viaja.
 */
export function directMessage(payload: DirectMessagePayload, me: string): SocialTransform {
  const message = payload.message;
  const peer = message.from === me ? message.to : message.from;
  const mine = message.from === me;

  return (state) => {
    const thread = state.threads[peer];
    const open = state.activePeer === peer;
    // Ya estaba: ni se vuelve a contar ni se vuelve a guardar.
    if (thread?.messages.some((entry) => entry.id === message.id)) return state;

    return {
      ...state,
      threads: thread ? { ...state.threads, [peer]: mergeMessage(thread, message) } : state.threads,
      // Se reordena: un mensaje nuevo sube esa conversación, que es lo que la lista promete.
      friends: sortFriends(
        state.friends.map((friend) =>
          friend.id !== peer
            ? friend
            : {
                ...friend,
                lastMessageAt: Math.max(friend.lastMessageAt ?? 0, message.at),
                unread: mine || open ? friend.unread : friend.unread + 1,
              },
        ),
      ),
    };
  };
}

/** Una conversación quedó leída. Llega a las demás pestañas de quien la leyó. */
export function conversationRead(peerId: string): SocialTransform {
  return (state) => ({
    ...state,
    friends: state.friends.map((friend) =>
      friend.id === peerId ? { ...friend, unread: 0 } : friend,
    ),
  });
}

// --------------------------------------------------- foto de la base

/**
 * Escribe la foto de la base encima del estado.
 *
 * **Sin mezclar nada.** Sólo se llama cuando ningún aviso llegó durante su viaje, así
 * que es autoritativa por construcción: el servidor escribe en SQLite ANTES de emitir,
 * de modo que un viaje sin avisos significa que todo lo aplicado en vivo ya estaba
 * commiteado cuando se armó la foto. Quien comprueba esa condición es `useSocial`.
 *
 * Los hilos no se tocan: la foto no los trae.
 */
export function applySnapshot(state: SocialState, snapshot: SocialSnapshot): SocialState {
  return {
    ...state,
    friends: sortFriends(snapshot.friends),
    incoming: snapshot.incoming,
    outgoing: snapshot.outgoing,
    clubs: snapshot.clubs,
    ready: true,
  };
}

// --------------------------------------------------- la ronda limpia

export type SnapshotOutcome = "applied" | "dirty" | "failed";

export interface StableSnapshot {
  outcome: SnapshotOutcome;
  /** La foto que se puede aplicar, o null si ninguna ronda salió limpia. */
  snapshot: SocialSnapshot | null;
}

/**
 * Pide la foto de la base hasta conseguir una ronda en la que NO llegó ningún aviso.
 *
 * `revision` es un contador de hechos aplicados en vivo. Si cambia mientras la foto
 * viaja, esa foto pudo armarse antes del aviso y escribirla encima lo borraría: se
 * descarta y se vuelve a pedir. Una ronda sin avisos es autoritativa por construcción,
 * porque el servidor escribe en SQLite ANTES de emitir — todo lo aplicado en vivo ya
 * estaba commiteado cuando se armó.
 *
 * Comparar horas en vez de contar hechos no serviría: `Date.now()` tiene resolución de
 * milisegundo, así que "la foto ya lo traía" y "llegó después" pueden compartir marca.
 *
 * Si ninguna ronda sale limpia devuelve `dirty` **sin foto**. Aplicar la última de todos
 * modos sería volver a meter el bug que esto existe para evitar; quien llama reintenta.
 */
export async function stableSnapshot(
  fetchSnapshot: () => Promise<SocialSnapshot>,
  revision: () => number,
  rounds: number,
): Promise<StableSnapshot> {
  for (let round = 0; round < rounds; round++) {
    const before = revision();

    let snapshot: SocialSnapshot;
    try {
      snapshot = await fetchSnapshot();
    } catch {
      return { outcome: "failed", snapshot: null };
    }

    if (revision() === before) return { outcome: "applied", snapshot };
  }

  return { outcome: "dirty", snapshot: null };
}

// --------------------------------------------------- relación con otra persona

/**
 * En qué estás con alguien, SIEMPRE derivado del estado vigente.
 *
 * Se calcula en vez de guardarse porque una relación guardada envejece en pantalla: el
 * resultado de una búsqueda decía "ninguna" después de que ya le habías mandado la
 * solicitud, y el botón "Agregar" seguía ahí invitando a repetirla.
 */
export function relationOf(state: SocialState, me: string, personId: string): FriendRelation {
  if (personId === me) return "self";
  if (state.friends.some((friend) => friend.id === personId)) return "friend";
  if (state.outgoing.some((entry) => entry.to.id === personId)) return "request_sent";
  if (state.incoming.some((entry) => entry.from.id === personId)) return "request_received";
  return "none";
}
